import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkOrdersStore } from "../../stores/work_orders";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "./Modal";

const hasExtractedData = (data) =>
  !!(
    data.scheduled_from ||
    data.scheduled_until ||
    data.availability_from ||
    data.availability_until ||
    data.customer ||
    data.customer_property ||
    data.summary
  );

// Paste dialog for the work order assistant: sends pasted text (email,
// call note, ticket, …) to the backend extraction endpoint and hands the
// structured result plus the exact submitted source text to the parent.
function AssistantDialog({ initialText, onApply, onClose }) {
  const { t } = useTranslation();
  const extractFromText = useWorkOrdersStore((state) => state.extractFromText);
  const [text, setText] = useState(initialText || "");
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorKey, setErrorKey] = useState(null);

  const handleExtract = async () => {
    setErrorKey(null);
    setIsProcessing(true);
    const { data, error } = await extractFromText({ text });
    setIsProcessing(false);
    if (error) {
      setErrorKey(error?.detail?.[0]?.t || "assistant.errors.unavailable");
      return;
    }
    if (!hasExtractedData(data)) {
      setErrorKey("assistant.errors.nothing_found");
      return;
    }
    onApply(data, text);
  };

  const applyDisabled = isProcessing || !text.trim();
  const applyIconClass = isProcessing ? "fa fa-spinner fa-spin fa-fw me-1" : "fa fa-wand-magic-sparkles fa-fw me-1";
  const processLabel = isProcessing ? t("assistant.processing") : t("assistant.process");

  return (
    <Modal size="lg">
      <ModalHeader>
        <h5 className="modal-title">
          <i className="fa fa-wand-magic-sparkles fa-fw me-1" />
          {t("assistant.title")}
        </h5>
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small">{t("assistant.hint")}</p>
        <textarea
          className="form-control fs-13px"
          rows={12}
          maxLength={20000}
          autoFocus
          placeholder={t("assistant.placeholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={isProcessing}
        />
        {errorKey && <div className="alert alert-danger mt-3 mb-0">{t(errorKey)}</div>}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-default" onClick={onClose} disabled={isProcessing}>
          {t("menu.cancel")}
        </button>
        <button type="button" className="btn btn-success" onClick={handleExtract} disabled={applyDisabled}>
          <i className={applyIconClass} />
          {processLabel}
        </button>
      </ModalFooter>
    </Modal>
  );
}

AssistantDialog.propTypes = {
  initialText: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AssistantDialog;
